import { Component, DoCheck, inject, OnDestroy, OnInit } from '@angular/core';
import { NbToastrService } from '@nebular/theme';
import { Store } from '@ngxs/store';
import { Observable, Subject, takeUntil } from 'rxjs';
import { UtilityService } from 'src/app/core/services/utility.service';
import { InsightsState } from 'src/app/features/insights/redux/insights.state';
import { Campaign } from 'src/app/models/campaign';
import { Insight } from 'src/app/models/insight';
import { ChangedSpaceEvent } from 'src/app/models/qa-models/changed-space-event';
import { FavoriteRecommendation } from 'src/app/models/qa-models/favorite-recommendation';
import { ImageReview } from 'src/app/models/qa-models/image-review';
import { ObjectZoneRecommendation, ObservedZone } from 'src/app/models/qa-models/object-zone-recommendation';
import { QAInsightEvent } from 'src/app/models/qa-models/qa-models';
import {
  RegionRecommendations,
  RegionSchedule,
  SmartScheduleRecommendation
} from 'src/app/models/qa-models/schedule-recommendation';
import { Robot } from 'src/app/models/robot';
import { SharedModule } from 'src/app/shared/shared.module';
import { CampaignState } from '../../../campaigns/redux';
import { QaClFormComponent } from '../../forms/qa-cl-form/qa-cl-form.component';
import { QaFavoriteFormComponent } from '../../forms/qa-favorites/qa-favorite-form.component';
import { QaImageFormComponent } from '../../forms/qa-image-form/qa-image-form.component';
import { QaSSchedulingComponent } from '../../forms/qa-s-scheduling/qa-s-scheduling.component';
import { QaState } from '../../redux';
import { QaServiceService } from '../../services/qa-service.service';

@Component({
  selector: 'app-common-bots',
  templateUrl: './common-bots.component.html',
  styleUrls: ['./common-bots.component.css'],
  standalone: true,
  imports: [SharedModule, QaClFormComponent, QaFavoriteFormComponent, QaImageFormComponent, QaSSchedulingComponent]
})
export class CommonBotsComponent implements OnInit, DoCheck, OnDestroy {
  public campaign$: Observable<any[]> = inject(Store).select(CampaignState.campaign);
  public insight$: Observable<any[]> = inject(Store).select(InsightsState.insightsActive);
  public robot$: Observable<any[]> = inject(Store).select(QaState.robots);
  public ownerRobot$: Observable<any[]> = inject(Store).select(QaState.ownerRobots);
  private unsubscribe: Subject<void> = new Subject();

  public spin;
  public formRobotData: Robot[];
  public default_timezone = 'America/Chicago';
  public insights: Insight[];

  public selectedRobots: any[] = [];
  public campaigns: Campaign[];

  public ownerRobots = [];
  public selectedCampaigns = {};

  public additionalFormValues;
  public regions = {};

  public submitBtn_enable = false;
  public submitBtn_show = false;

  constructor(
    public service: QaServiceService,
    private toastrService: NbToastrService,
    public utility: UtilityService
  ) {}

  public ngOnInit() {
    this.insight$.pipe(takeUntil(this.unsubscribe)).subscribe((data) => (this.insights = data));
    this.robot$
      .pipe(takeUntil(this.unsubscribe))
      .subscribe((data) => (this.formRobotData = JSON.parse(JSON.stringify(data))));
    this.campaign$
      .pipe(takeUntil(this.unsubscribe))
      .subscribe((data) => (this.campaigns = data.slice().sort((a, b) => (a.created_dttm > b.created_dttm ? -1 : 1))));
    this.ownerRobot$
      .pipe(takeUntil(this.unsubscribe))
      .subscribe((data) => (this.ownerRobots = JSON.parse(JSON.stringify(data))));
  }

  public ngOnDestroy(): void {
    this.unsubscribe.next();
    this.unsubscribe.complete();
  }

  public ngDoCheck(): void {
    //Called every time that the input properties of a component or a directive are checked. Use it to extend change detection by performing a custom check.
    //Add 'implements DoCheck' to the class.

    this.submitBtn_show = this.showSubmitButton();
    this.submitBtn_enable = this.disableSubmitButton();
  }

  /******************************************************
   * Submit button enabling functions for both common
   * and KOZR robots
   *****************************************************/
  public showSubmitButton() {
    let verify = false;

    if (this.selectedRobots.length > 0) {
      verify = this.selectedRobots.every(
        (robot) => this.selectedCampaigns[robot.id] && this.selectedCampaigns[robot.id].length !== 0
      );
    }
    // This resets the "Notification submission successful" modal to null
    // before the next submission takes place
    if (!verify) {
      this.service.submittedNotification = null;
    }

    return verify;
  }

  /******************************************************
   * Serves to check if the submit button should be
   * enabled in the HTML or not
   *****************************************************/
  public disableSubmitButton() {
    return this.selectedRobots.some((robot) => {
      if (!this.selectedCampaigns[robot.id] || this.selectedCampaigns[robot.id].length === 0) {
        return true;
      }

      return false;
    });
  }
  public groupByFn = (item: any) => {
    return this.ownerRobots?.some((s: any) => s.id === item.id) ? 'My Robots' : 'Common Robots';
  };

  public onScheduleChanged(robot, regions) {
    this.regions[robot.id] = regions;
  }

  public onRegionChanged(robot, regions) {
    this.regions[robot.id] = regions;
  }

  public seasonalChanged(event) {
    return;
  }

  /******************************************************
   * Submit notification method.
   *
   * This method uses the qa-service to parse the
   * notification, which is then also parsed through
   * to utility.service for a different endpoint.
   *****************************************************/
  public createQANotification() {
    this.service.submittedNotification = null;
    const notifications = [];
    this.selectedRobots.map((robot) => {
      this.selectedCampaigns[robot.id].map((campaign) => {
        try {
          const notification = this.createDefaultQANotification(campaign.id, robot);
          switch (campaign.campaign_type) {
            case 'SpaceChanged':
              const changedSpaceEventObject = this.createChangedSpaceEventObject();
              notification.changed_space_event = changedSpaceEventObject;

              break;
            case 'ImageReview':
              const imageReviewObject = this.createImageReviewObject();
              notification.image_review = imageReviewObject;

              break;
            case 'ObjectZoneRecommendation':
              const objectZoneRecommendationObject = this.createObjectZoneRecommendationObject();
              notification.object_zone_recommendation = objectZoneRecommendationObject;

              break;
            case 'SmartScheduleRecommendation':
              const campaign_version = campaign.name.includes('v1') || campaign.name.includes('V1') ? '1.0' : '2.0';
              const scheduleRecommendation = this.createScheduleRecommendationObject(robot.id);
              // v1 and v2 differentiation done by campaign name
              scheduleRecommendation.schedulingVersion = campaign_version;
              notification.schedule_recommendation = scheduleRecommendation;

              break;
            case 'FavoriteRecommendation':
              const favoriteRecommendation = this.createFavoriteRecommendationObject();
              notification.favorite_recommendation = favoriteRecommendation;

              break;
            default:
              break;
          }
          this.service.submitNotification(notification);

          notifications.push(notification);
        } catch (error) {
          console.error(error);
          this.toastrService.show(status, $localize`Invalid input, check the input parameters!`, { status: 'warning' });
        }
      });
    });

    return notifications;
  }

  public createChangedSpaceEventObject() {
    const changedSpaceEvent = new ChangedSpaceEvent();
    changedSpaceEvent.pmap_id = this.additionalFormValues[0];
    changedSpaceEvent.pmapv_id = this.additionalFormValues[1];
    changedSpaceEvent.change_type = this.additionalFormValues[2];
    changedSpaceEvent.mission_number = this.additionalFormValues[3];
    changedSpaceEvent.mission_timestamp = this.additionalFormValues[4];

    return changedSpaceEvent;
  }

  public createFavoriteRecommendationObject() {
    const favoriteRecommendation = new FavoriteRecommendation();
    favoriteRecommendation.id = this.utility.createRandomMockID();
    favoriteRecommendation.display_order = 0;
    favoriteRecommendation.modification_secs = 0;
    favoriteRecommendation.robot_commanddefs = [];
    favoriteRecommendation.commanddefs = this.additionalFormValues.commanddefs;
    favoriteRecommendation.color = '#FFFFFF';

    return favoriteRecommendation;
  }

  public createImageReviewObject() {
    const imageReview = new ImageReview();
    imageReview.mission_id = this.additionalFormValues[0];

    return imageReview;
  }

  public createObzervedZone() {
    const zone = new ObservedZone();
    zone.zone_id = this.additionalFormValues[2];
    zone.region_id = this.additionalFormValues[3];
    zone.primary_related_object = this.additionalFormValues[4];
    zone.primary_object_type = this.additionalFormValues[5];
    zone.suggestion_id = this.additionalFormValues[6];
    zone.confidence = this.additionalFormValues[7];

    return [zone];
  }

  public createObjectZoneRecommendationObject() {
    const objectZoneRecommendation = new ObjectZoneRecommendation();
    objectZoneRecommendation.pmap_id = this.additionalFormValues[0];
    objectZoneRecommendation.pmapv_id = this.additionalFormValues[1];
    objectZoneRecommendation.last_user_pmapv_id = this.additionalFormValues[1];
    const zones = this.createObzervedZone();
    objectZoneRecommendation.zones = zones;

    return objectZoneRecommendation;
  }

  public createScheduleRecommendationObject(robot_id: string) {
    const scheduleRecommendation = new SmartScheduleRecommendation();
    scheduleRecommendation.id = this.utility.createRandomMockID();
    scheduleRecommendation.robotId = robot_id;
    scheduleRecommendation.timezone = this.default_timezone;
    const recommendedSchedule = this.createRegionRecommendations(robot_id);
    scheduleRecommendation.recommendedSchedule = recommendedSchedule;

    return scheduleRecommendation;
  }

  public createRegionRecommendations(robot_id: string) {
    const recommendedSchedule = new RegionRecommendations();

    const schedules = this.regions[robot_id].regions.map((region) => {
      const regionSchedule = new RegionSchedule();
      regionSchedule.pmap_id = region.pmap_id;
      regionSchedule.pmapv_id = region.pmapv_id;
      regionSchedule.region_id = region.region_id !== '' ? region.region_id : '__all__';
      regionSchedule.schedule = this.createSchedule(region);

      return regionSchedule;
    });

    recommendedSchedule.regions = schedules;

    return recommendedSchedule;
  }

  public createSchedule(region) {
    const schedule = {
      Mon: { start_time: null, score: null },
      Tue: { start_time: null, score: null },
      Wed: { start_time: null, score: null },
      Thur: { start_time: null, score: null },
      Fri: { start_time: null, score: null },
      Sat: { start_time: null, score: null },
      Sun: { start_time: null, score: null }
    };
    schedule[region.schedule.day].start_time = region.schedule.hour * 3600 + region.schedule.minute * 60;
    schedule[region.schedule.day].score = parseInt(region.schedule.score, 10);

    return schedule;
  }

  public createDefaultQANotification(campaign_id: string, robot: Robot) {
    const notification = new QAInsightEvent();
    notification.campaign_id = campaign_id;
    notification.robot_id = robot.id;
    notification.robot_name = robot.name;

    return notification;
  }

  /******************************************************
   * Receive values from an auto-generated form
   *****************************************************/
  public receiveChildParameters(event) {
    this.additionalFormValues = event;
  }
}
